import {
  CaretLeftIcon,
  CaretRightIcon,
  LightbulbIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '../common/utils/localStorage';

interface TipContent {
  title: string;
  description: string;
}

interface TipConfig {
  tips: TipContent[];
  preferenceKey: string;
  duration?: number;
}

function getToastPreference(key: string): boolean {
  return getFromLocalStorage(`spellcaster-toast-${key}`) || false;
}

function saveToastPreference(key: string, value: boolean) {
  saveToLocalStorage(`spellcaster-toast-${key}`, value);
}

function TipCarousel({
  tips,
  onDismiss,
}: {
  tips: TipContent[];
  onDismiss: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentTip = tips[currentIndex];
  const hasMultipleTips = tips.length > 1;

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % tips.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + tips.length) % tips.length);
  };

  return (
    <div className="space-y-3" tabIndex={0}>
      <div className="flex items-start gap-2">
        <LightbulbIcon
          className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0"
          weight="duotone"
        />
        <div className="flex-1">
          <h4 className="font-medium text-sm mb-1">{currentTip.title}</h4>
          <p className="text-sm text-muted-foreground">
            {currentTip.description}
          </p>
        </div>
      </div>

      {hasMultipleTips && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className="h-6 w-6 p-0"
              aria-label="Previous tip"
            >
              <CaretLeftIcon className="h-3 w-3" />
            </Button>

            <span className="text-xs text-muted-foreground px-2">
              {currentIndex + 1} of {tips.length}
            </span>

            <Button
              variant="ghost"
              size="sm"
              onClick={goToNext}
              disabled={currentIndex === tips.length - 1}
              className="h-6 w-6 p-0"
              aria-label="Next tip"
            >
              <CaretRightIcon className="h-3 w-3" />
            </Button>
          </div>

          <button
            onClick={onDismiss}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            aria-label="Don't show this tip again"
          >
            Don't show again
          </button>
        </div>
      )}

      {!hasMultipleTips && (
        <button
          onClick={onDismiss}
          className="text-xs text-muted-foreground hover:text-foreground underline"
          aria-label="Don't show this tip again"
        >
          Don't show again
        </button>
      )}
    </div>
  );
}

export function showTipToast(config: TipConfig) {
  if (getToastPreference(config.preferenceKey)) {
    return;
  }

  const toastId = toast('', {
    description: (
      <TipCarousel
        tips={config.tips}
        onDismiss={() => {
          saveToastPreference(config.preferenceKey, true);
          toast.dismiss(toastId);
        }}
      />
    ),
    duration: config.duration || 8000,
  });
}
